import React, { useState, useEffect } from 'react';
import { userService, organizationService } from '../services';
import { User, UserRole, UserStatus, Organization } from '../types';
import { useAuthStore } from '../store/authStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Check,
  X,
  Loader2,
  UserCheck,
  UserX,
  Plus,
  Eye,
  Edit,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  role: UserRole;
  password: string;
  organizationId?: string;
}

export const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'pending'>('all');
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.ORG_USER);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const { user: currentUser } = useAuthStore();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormData>();

  useEffect(() => {
    loadUsers(currentPage, pageSize);
    loadPendingUsers();
    loadOrganizations();
  }, [currentPage, pageSize]);

  const loadUsers = async (page: number = 0, size: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getAllUsers(page, size);
      setUsers(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    try {
      const response = await userService.getPendingUsers();
      setPendingUsers(response);
    } catch (err: any) {
      console.error('Failed to load pending users:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await organizationService.getAllOrganizations(0, 100);
      setOrganizations(response.content);
    } catch (err: any) {
      console.error('Failed to load organizations:', err);
    }
  };

  const handleApprove = async (userId: string) => {
    try {
      await userService.approveUser(userId);
      await loadUsers(currentPage, pageSize);
      await loadPendingUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to approve user');
    }
  };

  const handleReject = async (userId: string) => {
    try {
      await userService.rejectUser(userId);
      await loadPendingUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to reject user');
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: UserStatus) => {
    try {
      if (currentStatus === UserStatus.ACTIVE) {
        await userService.deactivateUser(userId);
      } else {
        await userService.activateUser(userId);
      }
      await loadUsers(currentPage, pageSize);
    } catch (err: any) {
      setError(err.message || 'Failed to update user status');
    }
  };

  const handleOpenDialog = async (user?: User) => {
    if (user) {
      setEditingUser(user);
      const [firstName, ...lastNameParts] = user.fullName.split(' ');
      setSelectedRole(user.role);
      setSelectedOrganizationId(user.organizationId ? String(user.organizationId) : '');
      reset({
        firstName,
        lastName: lastNameParts.join(' '),
        email: user.email,
        phoneNumber: user.phoneNumber || '',
        role: user.role,
        password: '', // Password not required for editing
      });
    } else {
      setEditingUser(null);
      reset({});
      setSelectedRole(UserRole.ORG_USER);
      setSelectedOrganizationId('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setSelectedRole(UserRole.ORG_USER);
    setSelectedOrganizationId('');
    reset({});
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      if (editingUser) {
        await userService.updateUser(editingUser.id, {
          email: data.email,
          fullName: `${data.firstName} ${data.lastName}`,
          phoneNumber: data.phoneNumber,
          role: selectedRole,
          organizationId: selectedOrganizationId ? parseInt(selectedOrganizationId) : undefined,
        });
      } else {
        await userService.createUser({
          email: data.email,
          fullName: `${data.firstName} ${data.lastName}`,
          password: data.password,
          phoneNumber: data.phoneNumber,
          role: selectedRole,
          organizationId: selectedOrganizationId ? parseInt(selectedOrganizationId) : undefined,
        });
      }
      await loadUsers(currentPage, pageSize);
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || `Failed to ${editingUser ? 'update' : 'create'} user`);
    }
  };

  const getRoleBadgeVariant = (role: UserRole): 'default' | 'secondary' | 'outline' => {
    switch (role) {
      case UserRole.SYS_ADMIN:
        return 'default';
      case UserRole.ORG_ADMIN:
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: UserStatus): 'default' | 'secondary' | 'destructive' => {
    switch (status) {
      case UserStatus.ACTIVE:
        return 'default';
      case UserStatus.INACTIVE:
        return 'secondary';
      case UserStatus.PENDING_APPROVAL:
        return 'secondary';
      default:
        return 'destructive';
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground">Manage users and pending approvals</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'all'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          All Users ({totalElements})
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'pending'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Pending Approvals ({pendingUsers.length})
        </button>
      </div>

      {/* All Users Tab */}
      {activeTab === 'all' && (
        <Card>
          <CardHeader>
            <CardTitle>All Users</CardTitle>
            <CardDescription>List of all registered users in the system</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.profilePictureUrl} alt={user.fullName} />
                          <AvatarFallback>
                            {user.fullName
                              ?.split(' ')
                              .map((n) => n[0])
                              .join('')
                              .toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {user.fullName}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)}>{user.role}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.organizationName || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(user.status)}>{user.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(user)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {user.status === UserStatus.ACTIVE ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(user.id, user.status)}
                            disabled={user.id === currentUser?.id}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(user.id, user.status)}
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Activate
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {users.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">No users found</p>
            )}

            {/* Pagination Controls */}
            {totalPages > 0 && (
              <div className="flex items-center justify-between border-t pt-4 mt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    Showing {currentPage * pageSize + 1} to{' '}
                    {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} users
                  </span>
                  <span className="mx-2">|</span>
                  <span>Rows per page:</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      setCurrentPage(0);
                    }}
                  >
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(0)}
                    disabled={currentPage === 0}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-3 text-sm">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Approvals</CardTitle>
            <CardDescription>Users awaiting approval</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.profilePictureUrl} alt={user.fullName} />
                          <AvatarFallback>
                            {user.fullName
                              ?.split(' ')
                              .map((n) => n[0])
                              .join('')
                              .toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {user.fullName}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)}>{user.role}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.organizationName || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="default" size="sm" onClick={() => handleApprove(user.id)}>
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleReject(user.id)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {pendingUsers.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">No pending approvals</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit User Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user information' : 'Add a new user to the system'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input id="firstName" {...register('firstName', { required: true })} />
                {errors.firstName && <p className="text-sm text-destructive">Required</p>}
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input id="lastName" {...register('lastName', { required: true })} />
                {errors.lastName && <p className="text-sm text-destructive">Required</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" {...register('email', { required: true })} />
              {errors.email && <p className="text-sm text-destructive">Required</p>}
            </div>

            <div>
              <Label htmlFor="phoneNumber">Phone Number *</Label>
              <Input id="phoneNumber" {...register('phoneNumber', { required: true })} />
              {errors.phoneNumber && <p className="text-sm text-destructive">Required</p>}
            </div>

            {!editingUser && (
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  {...register('password', { required: !editingUser, minLength: 6 })}
                />
                {errors.password && <p className="text-sm text-destructive">Min 6 characters</p>}
              </div>
            )}

            <div>
              <Label htmlFor="role">Role *</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as UserRole)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UserRole.SYS_ADMIN}>System Admin</SelectItem>
                  <SelectItem value={UserRole.SYS_SUPPORT}>System Support</SelectItem>
                  <SelectItem value={UserRole.ORG_ADMIN}>Organization Admin</SelectItem>
                  <SelectItem value={UserRole.ORG_USER}>Organization User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(selectedRole === UserRole.ORG_ADMIN || selectedRole === UserRole.ORG_USER) && (
              <div>
                <Label htmlFor="organization">Organization *</Label>
                <Select value={selectedOrganizationId} onValueChange={setSelectedOrganizationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">{editingUser ? 'Update' : 'Create'} User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
